/** @file The auto-rig session's ports in the editor: REST client, doc store, viewport ghost store, clock. */
import type { ApiClient } from '../../api/createApiClient';
import { whenConfirmed } from '../../api/sync/whenConfirmed';
import { docStore } from '../../stores/docStore';
import { ghostStore } from '../../stores/ghostStore';
import type { AutorigPorts } from './autorigTypes';

type Client = Pick<ApiClient, 'autorig' | 'applyCommands' | 'revert'>;

/** The ghost owner this panel writes; it never clears another panel's ghost. */
const OWNER = 'autorig';

/** The open project's slice, or null when another project (or none) is open. */
const sliceOf = (projectId: string) => {
  const state = docStore.getState();
  return state.projectId === projectId ? state.slice : null;
};

/**
 * Apply = one `autorig.applyPlan` commit of the reviewed plan (W2-39), never a second analysis. Undo is the REST
 * client's revert (it answers the revert's rev and rejects on a refusal, which the session keeps on its banner), not
 * `history.revert` (void, refusals become notices). Waiting for the editor's own copy is `whenConfirmed` (W3-72).
 */
export function autorigPorts(client: Client, projectId: string): AutorigPorts {
  return {
    analyse: async (fn, args) => {
      const reply = await client.autorig(projectId, fn, { args, apply: false });
      return { plan: reply.plan, rev: reply.rev };
    },
    applyPlan: (plan, ifRev) => client.applyCommands(projectId, [{ name: 'autorig.applyPlan', input: { plan } }], ifRev === undefined ? {} : { ifRev }),
    revert: (rev) => client.revert(projectId, rev),
    currentDoc: () => sliceOf(projectId)?.confirmed ?? null,
    confirmedRev: () => sliceOf(projectId)?.rev ?? null,
    showGhost: (ghost) => {
      if (ghost !== null || ghostStore.getState().ghost?.owner === OWNER) ghostStore.setState({ ghost });
    },
    confirmed: whenConfirmed,
    now: () => new Date().toISOString(),
  };
}
